import React from 'react';
import { Selected } from '../AbstractComboBox/hooks/usePopupKeyboardNavigation';
import { usePopup } from '../AbstractComboBox/hooks/usePopup';
import ListItemEmpty from '../AbstractComboBox/ListItemEmpty/ListItemEmpty';
import { Section } from '../AbstractComboBox/types/Section';
import { IEntity } from '../../../../../../types/api/entities/IEntity';
import { CategoryListItem } from './CategoryListItem';
import {
  ComboBoxInputFixedValueProps,
  useComboBoxInputFixedValue,
} from '../AbstractComboBox/hooks/useComboBoxInputFixedValue';
import {
  AbstractFormsComboBoxFixedValue,
  ComboBoxFixedValueProps,
} from '../AbstractComboBox/AbstractFormsComboBoxFixedValue';
import ListBoxModifier from '../AbstractComboBox/Listbox/ListBox.modifier';

type CategoriesComboBoxProps = ComboBoxFixedValueProps &
  ComboBoxInputFixedValueProps & {
    onSelection: (item?: Category) => void;
    options: Category[];
  };

export type Category = {
  value: string;
  text: string;
  hexColor?: string;
};

interface MappedCategory extends IEntity {
  type: 'categories';
  title: string;
  hexColor?: string;
}

const transformCategoryOptions = (categories: Category[]): Section<MappedCategory> => {
  const mappedResults: MappedCategory[] = categories.map(category => ({
    id: category.value,
    title: category.text,
    hexColor: category.hexColor,
    type: 'categories',
  }));

  return {
    key: 'categories',
    results: mappedResults,
  };
};

/**
 * Methode to filter available options with user input.
 */
const filterCategoriesByInput = (categories: Category[], inputValue: string | undefined) => {
  return !inputValue
    ? categories
    : categories.filter(category => category.text.toLowerCase().includes(inputValue.toLowerCase()));
};

export function CategoriesComboBox(props: CategoriesComboBoxProps) {
  const popupProps = usePopup();
  const inputProps = useComboBoxInputFixedValue();

  const onSelection = ({ event, selectedItem }: Selected<MappedCategory>) => {
    event.preventDefault();
    if (selectedItem && typeof selectedItem !== 'string' && 'id' in selectedItem) {
      props.onSelection({ hexColor: selectedItem.hexColor, text: selectedItem.title, value: selectedItem.id });
      popupProps.closeResults();
    }
  };

  const filteredCategories = filterCategoriesByInput(props.options, inputProps.inputValue);
  const transformedCategoryOptions = transformCategoryOptions(filteredCategories);

  const renderListItem = (item: MappedCategory) => <CategoryListItem title={item.title} hexColor={item.hexColor} />;

  return (
    <AbstractFormsComboBoxFixedValue
      {...inputProps}
      {...popupProps}
      labelTitle={props.labelTitle}
      id={props.id}
      results={[transformedCategoryOptions]}
      onSelection={onSelection}
      modifierListBox={ListBoxModifier.MAX_HEIGHT_200}
    >
      {item => (item ? renderListItem(item) : <ListItemEmpty />)}
    </AbstractFormsComboBoxFixedValue>
  );
}
